import * as _ from "lodash-es";
import {Class} from "../../../../../../lib/legacyApi/fetchClassesForCourseByCampusId";
import pluralize from "pluralize";
import AvailableSessionsInstructor from "./AvailableSessionsInstructor";
import ClassSchedule from "./ClassSchedule";
import {FCWithHTMLAttributes} from "../../../../../types/reactTypes";
import {
	DayOfWeek,
	DAY_OF_WEEK_MAP,
} from "@/shared/legacyTypes/fetchClassesTypes";
import {CTAButtonForClass} from "./CTAButtonForClass";

interface AvailableSessionsClassListProps {
	classes: Class[];
	selectedDays?: Set<DayOfWeek>;
}

export const ClassRow = ({myClass}: {myClass: Class}) => {
	return (
		<div className="grid grid-cols-[1fr_9rem] items-center px-4 py-6 md:px-6">
			<div className="flex flex-col gap-2 md:grid md:grid-cols-2 md:items-center">
				<ClassSchedule class={myClass} />
				<AvailableSessionsInstructor
					instructor={myClass.instructor}
					fallbackTeacher={myClass.teacher}
				/>
			</div>
			<CTAButtonForClass classInfo={myClass} />
		</div>
	);
};

const MON_FRI = "Mon–Fri";
const TBD = "Schedule TBD";
const DAYS_ORDER = [
	MON_FRI,
	...Object.values(DAY_OF_WEEK_MAP).map((day) => pluralize(day)),
	TBD,
];

/**
 * Group classes by day of the week, then sort the days.
 *
 * We don't sort the classes here since the API already does that.
 */
const groupAndSortClasses = (
	classes: Class[],
	selectedDays: Set<DayOfWeek> = new Set()
) => {
	const classesByDay = _.groupBy(classes, (myClass) => {
		if (myClass.dow.length === 5) {
			return MON_FRI;
		} else if (myClass.dow.length === 1) {
			return pluralize(myClass.dow[0]);
		} else {
			return TBD;
		}
	});
	const dayClassesPairs = Object.entries(classesByDay);
	const selectedDaysArray = Array.from(selectedDays);
	const sortedDayClassesPairs = _.sortBy(
		dayClassesPairs,
		([day, classesOnDay]) => {
			let priority = DAYS_ORDER.indexOf(day);

			// increase priority if the classes are on one of the selected days
			const dayIntersectionWithSelectedDays = _.intersection(
				selectedDaysArray,
				classesOnDay[0].dow
			);
			if (dayIntersectionWithSelectedDays.length > 0) {
				priority -= DAYS_ORDER.length;
			}

			return priority;
		}
	);
	return Object.fromEntries(sortedDayClassesPairs);
};

const AvailableSessionsClassList: FCWithHTMLAttributes<
	AvailableSessionsClassListProps
> = ({classes, selectedDays, ...props}) => {
	const classesByDay = groupAndSortClasses(classes, selectedDays);
	return (
		<div {...props}>
			{_.map(classesByDay, (classesOnDay, day) => (
				<div key={day}>
					<div className="bg-light-green px-6 py-3 font-overline">
						{day.toLocaleUpperCase()}
					</div>
					{classesOnDay.map((myClass) => (
						<ClassRow myClass={myClass} key={myClass.classId} />
					))}
				</div>
			))}
		</div>
	);
};

export default AvailableSessionsClassList;
